// File: src/pages/seller/AddProduct/sections/ShippingTerms.jsx
import React, { useEffect, useRef } from "react";
import { useFormContext, Controller } from "react-hook-form";
import { CRow, CCol, CFormLabel, CFormInput, CFormSelect } from "@coreui/react";
import { loadGoogleMaps } from "../../../../utils/loadGoogleMaps";

const ShippingTerms = ({ setValue }) => {
  const {
    control,
    register,
    watch,
    formState: { errors },
  } = useFormContext();

  const autocompleteRef = useRef(null);
  const inputRef = useRef(null);
  const incoterm = watch("incoterm");
  const paymentTerm = watch("payment_term");

  const INCOTERM_RULES = {
    EXW: { port: false },
    FOB: { port: true },
    DDP: { port: true },
  };

  // Initialize Google Places Autocomplete for worldwide ports
  useEffect(() => {
    if (!inputRef.current) return;

    loadGoogleMaps().then((google) => {
      const autocomplete = new google.maps.places.Autocomplete(
        inputRef.current,
        { types: ["geocode"] }
      );

      autocomplete.addListener("place_changed", () => {
        const place = autocomplete.getPlace();
        if (place?.formatted_address) {
          setValue("delivery_port", place.formatted_address, {
            shouldValidate: true,
          });
          setValue("delivery_latitude", place.geometry.location.lat());
          setValue("delivery_longitude", place.geometry.location.lng());
        }
      });

      autocompleteRef.current = autocomplete;
    });
  }, [setValue, incoterm]);

  const firstRender = useRef(true);

  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }

    if (!["FOB", "DDP"].includes(incoterm)) {
      setValue("delivery_port", "");
    }
  }, [incoterm, setValue]);

  return (
    <div
      style={{
        border: "1px solid #dcdcdc",
        borderRadius: "8px",
        padding: "1.5rem",
        position: "relative",
        marginBottom: "1.5rem",
      }}
    >
      {/* Section Title */}
      <span
        style={{
          position: "absolute",
          top: "-0.8rem",
          left: "1rem",
          backgroundColor: "white",
          padding: "0 0.5rem",
          fontWeight: "600",
          color: "#333",
          fontSize: "1rem",
        }}
      >
        Shipping & Trade Terms
      </span>

      <CRow className="mb-3">
        <CCol md={3}>
          <CFormLabel>Incoterms</CFormLabel>
          <Controller
            name="incoterm"
            control={control}
            render={({ field }) => (
              <CFormSelect
                {...field}
                options={[
                  { label: "Select", value: "" },
                  { label: "FOB (Free On Board)", value: "FOB" },
                  // { label: "CIF", value: "CIF" },
                  { label: "EXW (Ex Works)", value: "EXW" },
                  { label: "DDP(Delivered Duty Paid) ", value: "DDP" },
                  // { label: "DAP", value: "DAP" },
                ]}
              />
            )}
          />
          {errors.incoterm && (
            <div style={{ color: "red" }}>{errors.incoterm.message}</div>
          )}
        </CCol>

        <CCol md={3}>
          <CFormLabel>Lead Time to Dispatch (days)</CFormLabel>
          <CFormInput
            type="number"
            {...register("lead_time")}
            placeholder="Enter number of days"
            min="1"
            onKeyDown={(e) =>
              ["e", "E", "+", "-", "."].includes(e.key) && e.preventDefault()
            }
          />
          {errors.lead_time && (
            <div style={{ color: "red" }}>{errors.lead_time.message}</div>
          )}
        </CCol>

        <CCol md={3}>
          <CFormLabel>Payment Terms</CFormLabel>
          <Controller
            name="payment_term"
            control={control}
            render={({ field }) => (
              <CFormSelect
                {...field}
                options={[
                  { label: "Select", value: "" },
                  { label: "Bank Wire Transfer", value: "bank_wire" },
                  { label: "Stripe", value: "stripe" },

                  // { label: "Advance", value: "Advance" },
                  // { label: "TT / Remittance", value: "TT / Remittance" },
                  // { label: "Net 30", value: "Net 30" },
                  // { label: "LC", value: "LC" },
                  // { label: "COD", value: "COD" },
                ]}
              />
            )}
          />
          {errors.payment_term && (
            <div style={{ color: "red" }}>{errors.payment_term.message}</div>
          )}
        </CCol>

        <CCol md={3}>
          <CFormLabel>Delivery Radius</CFormLabel>
          <Controller
            name="delivery_radius"
            control={control}
            render={({ field }) => (
              <CFormSelect
                {...field}
                options={[
                  { label: "Select", value: "" },
                  { label: "10 Km", value: "10" },
                  { label: "25 Km", value: "25" },
                  { label: "50 Km", value: "50" },
                  { label: "100 Km", value: "100" },
                  { label: "250 Km", value: "250" },
                  { label: "500 Km", value: "500" },
                ]}
              />
            )}
          />
          {errors.delivery_radius && (
            <div style={{ color: "red" }}>{errors.delivery_radius.message}</div>
          )}
        </CCol>
      </CRow>

      {INCOTERM_RULES[incoterm]?.port && (
        <CRow>
          <CCol md={12}>
            <CFormLabel>Delivery Location / Port</CFormLabel>
            <Controller
              name="delivery_port"
              control={control}
              render={({ field }) => (
                <CFormInput
                  {...field}
                  ref={(el) => {
                    field.ref(el);
                    inputRef.current = el;
                  }}
                  placeholder="Start typing to search ports worldwide"
                />
              )}
            />

            {errors.delivery_port && (
              <div style={{ color: "red" }}>{errors.delivery_port.message}</div>
            )}
          </CCol>
        </CRow>
      )}
    </div>
  );
};

export default React.memo(ShippingTerms);
